import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const MetaTagsInjector = () => {
    const location = useLocation();

    useEffect(() => {
        const fetchAndInject = async () => {
            try {
                const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
                const response = await axios.get(`${API_BASE_URL}/web-pages/meta-tags`);
                
                if (response.data.success) {
                    const { header_css, header_script, footer_css, footer_script } = response.data.data;

                    // 1. Inject Header CSS to <head>
                    if (header_css && header_css.trim()) {
                        let styleEl = document.getElementById('custom-header-css');
                        if (!styleEl) {
                            styleEl = document.createElement('style');
                            styleEl.id = 'custom-header-css';
                            styleEl.type = 'text/css';
                            document.head.appendChild(styleEl);
                        }
                        // Strip <style> tags if added by superadmin, as styleEl.textContent requires pure CSS content
                        styleEl.textContent = cleanCss(header_css);
                    } else {
                        const styleEl = document.getElementById('custom-header-css');
                        if (styleEl) styleEl.remove();
                    }

                    // 2. Inject Header Script to <head>
                    if (header_script && header_script.trim()) {
                        let scriptContainer = document.getElementById('custom-header-script-container');
                        if (scriptContainer) scriptContainer.remove();

                        scriptContainer = document.createElement('div');
                        scriptContainer.id = 'custom-header-script-container';
                        scriptContainer.style.display = 'none';
                        document.head.appendChild(scriptContainer);

                        injectAndExecuteScripts(header_script, scriptContainer);
                    } else {
                        const scriptContainer = document.getElementById('custom-header-script-container');
                        if (scriptContainer) scriptContainer.remove();
                    }

                    // 3. Inject Footer CSS & Script (only if footer element exists)
                    const footerEl = document.querySelector('footer');
                    if (footerEl) {
                        // Inject Footer CSS
                        if (footer_css && footer_css.trim()) {
                            let fStyleEl = document.getElementById('custom-footer-css');
                            if (!fStyleEl) {
                                fStyleEl = document.createElement('style');
                                fStyleEl.id = 'custom-footer-css';
                                fStyleEl.type = 'text/css';
                                footerEl.appendChild(fStyleEl);
                            }
                            fStyleEl.textContent = cleanCss(footer_css);
                        } else {
                            const fStyleEl = document.getElementById('custom-footer-css');
                            if (fStyleEl) fStyleEl.remove();
                        }

                        // Inject Footer Script
                        if (footer_script && footer_script.trim()) {
                            let fScriptContainer = document.getElementById('custom-footer-script-container');
                            if (fScriptContainer) fScriptContainer.remove();

                            fScriptContainer = document.createElement('div');
                            fScriptContainer.id = 'custom-footer-script-container';
                            fScriptContainer.style.display = 'none';
                            footerEl.appendChild(fScriptContainer);

                            injectAndExecuteScripts(footer_script, fScriptContainer);
                        } else {
                            const fScriptContainer = document.getElementById('custom-footer-script-container');
                            if (fScriptContainer) fScriptContainer.remove();
                        }
                    } else {
                        // Clean up footer tags when not on a page rendering <footer>
                        const fStyleEl = document.getElementById('custom-footer-css');
                        if (fStyleEl) fStyleEl.remove();
                        const fScriptContainer = document.getElementById('custom-footer-script-container');
                        if (fScriptContainer) fScriptContainer.remove();
                    }
                }
            } catch (error) {
                console.error("Error loading meta tags:", error);
            }
        };

        // Delay slightly to ensure page components & footer mount completely
        const timer = setTimeout(() => {
            fetchAndInject();
        }, 150);

        return () => clearTimeout(timer);
    }, [location.pathname]);

    // Helper to strip <style> tags if user entered them, leaving clean CSS content
    const cleanCss = (code) => {
        if (!code) return '';
        let clean = code.trim();
        if (clean.toLowerCase().startsWith('<style')) {
            // Strip opening tag
            clean = clean.replace(/^<style[^>]*>/i, '');
            // Strip closing tag
            clean = clean.replace(/<\/style>$/i, '');
        }
        return clean;
    };

    // Helper to parse scripts (with or without <script> tag) and execute them
    const injectAndExecuteScripts = (code, container) => {
        if (!code || !code.trim()) return;
        const trimmed = code.trim();

        // If it starts with <script, we parse it as HTML containing potential multiple script tags
        if (trimmed.toLowerCase().startsWith('<script')) {
            container.innerHTML = trimmed;
            const scriptTags = container.getElementsByTagName('script');
            Array.from(scriptTags).forEach(sTag => {
                const execScript = document.createElement('script');
                if (sTag.src) {
                    execScript.src = sTag.src;
                } else {
                    execScript.textContent = sTag.textContent;
                }
                container.appendChild(execScript);
            });
        } else {
            // Raw Javascript / jQuery code without <script> tag
            const execScript = document.createElement('script');
            execScript.textContent = trimmed;
            container.appendChild(execScript);
        }
    };

    return null;
};

export default MetaTagsInjector;
